<template>
  <div class="relative min-h-screen overflow-hidden">
    <!-- Background Image with multiply blend effect -->
    <div class="fixed inset-0 -z-30">
      <img src="/assets/photo.jpg" alt="Background" class="w-full h-full object-cover opacity-50 bg-image-animate" />
    </div>
    
    <!-- Primary multiply blend overlay for rich color interaction -->
    <div class="fixed inset-0 -z-20 bg-gradient-to-br from-blue-900/70 via-blue-900/70 to-gray-900/70 mix-blend-multiply"></div>
    
    <!-- Secondary overlay for better text readability -->
    <div class="fixed inset-0 -z-10 bg-black/40 backdrop-blur-[2px]"></div>
    
    <!-- Additional dark base layer -->
    <div class="fixed inset-0 bg-gray-900" style="z-index: -40;"></div>

    <!-- Navigation Header Component -->
    <NavHeader
      :dealerName="
        accountData && accountData[0]?.name ? accountData[0].name : dealerName
      "
    />

    <!-- Loader (from Form.vue) -->
    <div
      v-if="isLoading"
      class="isLoading flex justify-center items-center min-h-screen"
    >
      <div role="status">
        <svg
          aria-hidden="true"
          class="w-14 h-14 text-gray-600 animate-spin dark:text-gray-600 fill-blue-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Dashboard Header -->
    <div class="relative z-1 pt-8 pb-4">
      <div class="max-w-[48rem] lg:max-w-7xl px-4 sm:px-6 lg:px-8 mx-auto">
        <div class="text-center mb-8">
          <div class="mb-3 inline-block px-4 py-1">
            <p class="text-blue-400 font-medium">Customer Welcome Portal</p>
          </div>
          <h1
            class="text-3xl md:text-5xl font-bold text-white mb-3 bg-gradient-to-r from-blue-400 to-blue-500 bg-clip-text text-transparent"
          >
            Dashboard
          </h1>
          <p class="text-gray-300 text-lg mb-3">
            Track your performance and team activity
          </p>
        </div>
      </div>
    </div>

    <!-- No Data State - Show Single Widget -->
    <div
      v-if="accountData && accountData[0]?.form && (!chartData || !chartData.total_count)"
      class="max-w-[48rem] lg:max-w-7xl px-4 py-6 sm:px-6 lg:px-8 mx-auto relative z-1"
    >
      <!-- Date Range Presets -->
      <div class="mb-8">
        <div class="flex flex-wrap justify-center gap-2 lg:gap-3">
          <button
            @click="applyDatePreset('all')"
            class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
            :class="
              currentPreset === 'all'
                ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
                : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
            "
          >
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            <span>All Time</span>
          </button>

          <button
            @click="applyDatePreset('last7')"
            class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
            :class="
              currentPreset === 'last7'
                ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
                : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
            "
          >
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>Last 7 days</span>
          </button>

          <button
            @click="applyDatePreset('last30')"
            class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
            :class="
              currentPreset === 'last30'
                ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
                : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
            "
          >
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>Last 30 days</span>
          </button>

          <button
            @click="applyDatePreset('thisMonth')"
            class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
            :class="
              currentPreset === 'thisMonth'
                ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
                : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
            "
          >
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>This month</span>
          </button>

          <button
            @click="applyDatePreset('lastMonth')"
            class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
            :class="
              currentPreset === 'lastMonth'
                ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
                : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
            "
          >
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>Last month</span>
          </button>

          <button
            @click="applyDatePreset('last12Months')"
            class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
            :class="
              currentPreset === 'last12Months'
                ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
                : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
            "
          >
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>12 months</span>
          </button>
        </div>
      </div>

      <div class="p-12 bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl shadow-2xl text-center">
        <div class="mb-6">
          <div
            class="mx-auto w-24 h-24 bg-gradient-to-t from-blue-500 to-blue-600 rounded-3xl flex items-center justify-center"
          >
            <svg
              class="w-12 h-12 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
        </div>
        <h3 class="text-4xl font-bold text-white mb-4">No data available</h3>
        <p class="text-gray-300 text-lg mb-8">Try selecting a different date range.</p>
        
        <!-- Submit Button (from form) -->
        <router-link
          :to="'/account/' + $route.params.slug"
          class="inline-flex justify-center items-center grow py-3 px-8 pl-5 bg-blue-600 rounded-full hover:bg-blue-700 transition-all duration-300 shadow-lg"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke-width="1.5"
            stroke="currentColor" 
            class="flex-shrink-0 h-10 w-10 text-gray-100"
          >
            <path 
              stroke-linecap="round" 
              stroke-linejoin="round"
              d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" 
            />
          </svg>
          <span class="ml-4">
            <span class="block text-md font-semibold text-white">
              Send Your Onboarding
            </span>
            <span class="block text-xs text-gray-100">
              Message Delivery
            </span>
          </span>
        </router-link>
      </div>
    </div>

    <div
      v-if="accountData && accountData[0]?.form && chartData && chartData.total_count"
      class="max-w-[48rem] lg:max-w-7xl px-4 py-6 sm:px-6 lg:px-8 mx-auto relative z-1"
    >
      <!-- Date Range Presets -->
      <div class="mb-8">
        <div class="flex flex-wrap justify-center gap-2 lg:gap-3">
          <button
            @click="applyDatePreset('all')"
            class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
            :class="
              currentPreset === 'all'
                ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
                : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
            "
          >
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            <span>All Time</span>
          </button>

          <button
            @click="applyDatePreset('last7')"
            class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
            :class="
              currentPreset === 'last7'
                ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
                : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
            "
          >
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>Last 7 days</span>
          </button>

          <button
            @click="applyDatePreset('last30')"
            class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
            :class="
              currentPreset === 'last30'
                ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
                : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
            "
          >
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>Last 30 days</span>
          </button>

          <button
            @click="applyDatePreset('thisMonth')"
            class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
            :class="
              currentPreset === 'thisMonth'
                ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
                : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
            "
          >
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>This month</span>
          </button>

          <button
            @click="applyDatePreset('lastMonth')"
            class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
            :class="
              currentPreset === 'lastMonth'
                ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
                : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
            "
          >
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>Last month</span>
          </button>

          <button
            @click="applyDatePreset('last12Months')"
            class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
            :class="
              currentPreset === 'last12Months'
                ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
                : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
            "
          >
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>12 months</span>
          </button>
        </div>
      </div>

      <!-- Stats Cards with Enhanced Styling -->
      <!-- Top Row: SENDS and PERFORMANCE -->
      <div class="grid lg:grid-cols-2 gap-6 relative z-2 mb-6">
        <!-- Total Entries Count Component -->
        <div v-if="chartData && chartData.total_count">
          <total-entries-count
            :dateRange="selectedDateRange"
            :count="chartData.total_count"
            @update-form-title="handleTitleFromChild"
          />
        </div>
        <div
          v-else
          class="flex flex-col gap-y-3 lg:gap-y-5 p-6 md:p-8 bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl shadow-2xl transform transition-all duration-300 hover:scale-105"
        >
          <div class="text-center">
            <div class="mb-4">
              <div
                class="mx-auto w-16 h-16 bg-gradient-to-t from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center"
              >
                <svg
                  class="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
            </div>
            <h3
              class="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-2"
            >
              No Results
            </h3>
            <p class="text-gray-300 text-lg mb-4">at this time period.</p>
            <div
              class="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-4"
            >
              <dl
                class="flex justify-center items-center divide-x divide-white/20"
              >
                <dt class="pr-4">
                  <span class="block text-sm font-semibold text-gray-300"
                    >From: {{ selectedDateRange.startDate }}</span
                  >
                </dt>
                <dd class="text-left pl-4">
                  <span class="block text-sm font-semibold text-gray-300"
                    >To: {{ selectedDateRange.endDate }}</span
                  >
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <!-- Additional Stats Card -->
        <div
          class="flex flex-col gap-y-3 lg:gap-y-5 p-6 md:p-8 bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl shadow-2xl transform transition-all duration-300 hover:scale-105"
        >
          <div class="inline-flex justify-center items-center mb-2">
            <div
              class="w-3 h-3 inline-block bg-gradient-to-r from-green-400 to-green-600 rounded-full mr-3 animate-pulse"
            ></div>
            <span
              class="text-xs font-semibold uppercase tracking-wider text-gray-300"
              >Performance</span
            >
            <!-- Info Tooltip -->
            <div class="relative ml-2 group">
              <svg
                class="w-4 h-4 text-gray-400 hover:text-gray-300 cursor-help transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <!-- Tooltip Content -->
              <div
                class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-50"
              >
                <div
                  class="bg-gray-900 text-white text-xs rounded-lg py-3 px-5 shadow-lg border border-gray-700 min-w-[280px] max-w-sm"
                >
                  <div class="font-semibold mb-2">Goal Calculation:</div>
                  <div class="space-y-1 text-left">
                    <div v-if="currentPreset === 'all'">
                      <strong>All-Time Goal:</strong>
                      {{ goalTarget }} entries<br />
                      <span class="text-gray-300"
                        >(Based on 1 entry per day since Jan 1, 2000)</span
                      >
                    </div>
                    <div v-else-if="currentPreset === 'last12Months'">
                      <strong>Annual Goal:</strong> 360 entries (30/month × 12
                      months)
                    </div>
                    <div v-else-if="currentPreset === 'last7'">
                      <strong>Weekly Goal:</strong> 7 entries (1 per day × 7
                      days)
                    </div>
                    <div v-else-if="currentPreset === 'last30'">
                      <strong>Monthly Goal:</strong> 30 entries (1 per day × 30
                      days)
                    </div>
                    <div v-else-if="currentPreset === 'thisMonth'">
                      <strong>Month-to-Date Goal:</strong>
                      {{ goalTarget }} entries<br />
                      <span class="text-gray-300"
                        >({{ new Date().getDate() }} days × 1 per day)</span
                      >
                    </div>
                    <div v-else-if="currentPreset === 'lastMonth'">
                      <strong>Monthly Goal:</strong> 30 entries (full month
                      target)
                    </div>
                    <div v-else>
                      <strong>Period Goal:</strong>
                      {{ goalTarget }} entries<br />
                      <span class="text-gray-300"
                        >(Based on 1 entry per day)</span
                      >
                    </div>
                  </div>
                  <div class="mt-2 pt-2 border-t border-gray-600 text-gray-300">
                    Current: {{ chartData?.total_count || 0 }} entries
                  </div>
                  <!-- Tooltip Arrow -->
                  <div
                    class="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"
                  ></div>
                </div>
              </div>
            </div>
          </div>
          <div class="text-center">
            <div class="relative">
              <h3
                class="text-5xl sm:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent"
              >
                {{ goalProgressPercentage }}%
              </h3>
              <div
                class="absolute -top-2 -right-2 w-4 h-4 bg-green-400 rounded-full animate-bounce"
              ></div>
            </div>
            <p class="text-sm text-gray-300 mt-3 font-medium">
              {{
                currentPreset === "all"
                  ? "All-Time Goal Progress"
                  : currentPreset === "last12Months"
                  ? "Annual Goal Progress"
                  : currentPreset === "last7"
                  ? "Weekly Goal Progress"
                  : currentPreset === "thisMonth"
                  ? "Monthly Goal Progress (YTD)"
                  : currentPreset === "lastMonth"
                  ? "Monthly Goal Progress"
                  : currentPreset === "last30"
                  ? "Monthly Goal Progress"
                  : "Period Goal Progress"
              }}
            </p>
            <div
              class="mt-4 w-full bg-black/40 backdrop-blur-sm rounded-full h-2 border border-white/10"
            >
              <div
                class="bg-gradient-to-r from-green-400 to-blue-500 h-2 rounded-full transition-all duration-1000 ease-out"
                :style="{ width: `${goalProgressPercentage}%` }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Row: TOP MODELS (Full Width) -->
      <div class="relative z-2 mb-4">
        <!-- Most Popular Car Model Widget -->
        <div
          class="flex flex-col gap-y-3 lg:gap-y-5 p-6 md:p-8 bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl shadow-2xl transform transition-all duration-300 hover:scale-[1.02]"
        >
          <div class="flex justify-center items-center mb-2">
            <div
              class="w-3 h-3 inline-block bg-gradient-to-r from-blue-400 to-blue-600 rounded-full mr-3 animate-pulse"
            ></div>
            <span
              class="text-xs font-semibold uppercase tracking-wider text-gray-300"
              >Top Models</span
            >
          </div>
          
          <!-- No Data State -->
          <div v-if="!topCarModels[0] || topCarModels[0].model === 'N/A'" class="text-center py-8">
            <div class="mb-4">
              <div
                class="mx-auto w-16 h-16 bg-gradient-to-t from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center"
              >
                <svg
                  class="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
            </div>
            <h3 class="text-2xl font-bold text-white mb-2">No data available</h3>
            <p class="text-gray-300 text-sm">Start selling to see your top models!</p>
            <div class="mt-4 inline-block px-4 py-2 bg-blue-500/10 border border-blue-400/30 rounded-full">
              <span class="text-blue-300 text-sm font-medium">💪 Every sale counts!</span>
            </div>
          </div>

          <!-- Horizontal Layout for Top 3 -->
          <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <!-- First Place -->
            <div class="relative text-center">
              <div class="flex items-center justify-center gap-2 mb-2">
                <div class="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-500 flex items-center justify-center shadow-lg ring-2 ring-yellow-400/30">
                  <span class="text-sm font-bold text-gray-900">1</span>
                </div>
              </div>
              <h3 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-yellow-400 break-words px-2 drop-shadow-lg">
                {{ topCarModels[0].model }}
              </h3>
              <p class="text-sm text-gray-300 mt-2 font-medium">
                {{ topCarModels[0].count }} {{ topCarModels[0].count === 1 ? 'sale' : 'sales' }}
              </p>
            </div>

            <!-- Second Place -->
            <div v-if="topCarModels[1]" class="relative text-center md:border-l md:border-white/10">
              <div class="flex items-center justify-center gap-2 mb-2">
                <div class="w-7 h-7 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center shadow-lg ring-2 ring-gray-300/30">
                  <span class="text-sm font-bold text-gray-900">2</span>
                </div>
              </div>
              <h4 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-200 break-words px-2 drop-shadow-lg">
                {{ topCarModels[1].model }}
              </h4>
              <p class="text-sm text-gray-300 mt-2 font-medium">
                {{ topCarModels[1].count }} {{ topCarModels[1].count === 1 ? 'sale' : 'sales' }}
              </p>
            </div>

            <!-- Third Place -->
            <div v-if="topCarModels[2]" class="relative text-center md:border-l md:border-white/10">
              <div class="flex items-center justify-center gap-2 mb-2">
                <div class="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center shadow-lg ring-2 ring-orange-400/30">
                  <span class="text-sm font-bold text-white">3</span>
                </div>
              </div>
              <h4 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-orange-400 break-words px-2 drop-shadow-lg">
                {{ topCarModels[2].model }}
              </h4>
              <p class="text-sm text-gray-300 mt-2 font-medium">
                {{ topCarModels[2].count }} {{ topCarModels[2].count === 1 ? 'sale' : 'sales' }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="chartData && chartData.total_count"
      class="max-w-[48rem] lg:max-w-7xl px-4 pb-2 pt-6 sm:px-6 lg:px-8 mx-auto relative z-1"
    >
      <!-- Chart Section with Enhanced Header -->
      <div class="space-y-6">
        <div class="transform transition-all duration-300 hover:scale-[1.02]">
          <chart-weekly
            :chartData="chartData.entries"
            :dateRange="selectedDateRange"
            :currentPreset="currentPreset"
            @update-form-title="handleTitleFromChild"
          />
        </div>
      </div>
    </div>

    <div
      v-if="chartData && chartData.total_count"
      class="max-w-[48rem] lg:max-w-7xl px-4 py-6 pt-6 sm:px-6 lg:px-8 mx-auto relative z-1"
    >
      <!-- Sales Leaderboard Section -->
      <div class="transform transition-all duration-300 hover:scale-[1.02]">
        <sales-leader-board
          :dateRange="selectedDateRange"
          :entries="chartData.entries"
          :selectedSalesperson="selectedSalesperson"
          @update-form-title="handleTitleFromChild"
          @salesperson-selected="handleSalespersonSelected"
        />
      </div>
    </div>

    <!-- Total Entries List Component -->
    <total-entries-list
      v-if="chartData && chartData.total_count"
      :dateRange="selectedDateRange"
      :entries="chartData.entries"
      :selectedSalesperson="selectedSalesperson"
      :isDownloading="isDownloading"
      @update-form-title="handleTitleFromChild"
      @download-csv="downloadCSV"
      @clear-salesperson-filter="clearSalespersonFilter"
      @clear-date-range-filter="clearDateRangeFilter"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import TotalEntriesCount from "@/components/TotalEntriesCount.vue";
import TotalEntriesList from "@/components/TotalEntriesList.vue";
import ChartWeekly from "@/components/ChartWeekly.vue";
import salesLeaderBoard from "@/components/salesLeaderboard.vue";
import NavHeader from "@/components/NavHeader.vue";
//import GoogleReviews from '@/components/GoogleReviews.vue';
import axios from "axios";

export default {
  name: "welcome-dashboard",
  components: {
    TotalEntriesCount,
    TotalEntriesList,
    ChartWeekly,
    salesLeaderBoard,
    NavHeader,
    //GoogleReviews
  },
  data() {
    // Get the current date
    const currentDate = new Date();

    // Calculate the start date for last 30 days
    const startDate = new Date(currentDate);
    startDate.setDate(startDate.getDate() - 29); // 30 days including today

    return {
      selectedDateRange: {
        startDate: this.formatDate(startDate), // Start date is 30 days ago
        endDate: this.formatDate(currentDate), // End date is the current date
      },
      chartData: null, // Initialize chartData to null
      Entries: null,
      isLoading: false,
      currentPreset: "last30", // Default preset
      logoURLWH: "/assets/engagr-wh.svg",
      formTitle: "", // Initialize formTitle property
      selectedSalesperson: null, // For filtering by salesperson
      isDownloading: false, // For CSV download loading state
    };
  },

  watch: {
    accountData: function (newVal) {
      if (newVal && newVal[0]?.form) {
        this.fetchData(
          this.selectedDateRange.startDate,
          this.selectedDateRange.endDate
        );
      }
    },
  },

  computed: {
    ...mapState(["accountData"]),
    dealerName() {
      // Get dealer name from slug - convert slug format to display format
      const slug = this.$route.params.slug;
      if (slug) {
        // Convert slug like "courtney-patterson-ford" to "Courtney Patterson Ford"
        return slug
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      }
      return "Dealership";
    },

    // Calculate appropriate goal based on selected date range
    goalTarget() {
      if (
        !this.selectedDateRange.startDate ||
        !this.selectedDateRange.endDate
      ) {
        return 30; // Default monthly goal
      }

      const startDate = new Date(this.selectedDateRange.startDate);
      const endDate = new Date(this.selectedDateRange.endDate);
      const timeDiff = endDate.getTime() - startDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates

      // Base monthly goal is 30, calculate proportional goal based on time period
      const monthlyGoal = 30;
      const dailyGoal = monthlyGoal / 30; // approximately 1 per day

      switch (this.currentPreset) {
        case "all":
          // For "all time", calculate goal based on actual days in range
          return Math.round(dailyGoal * daysDiff);
        case "last12Months":
          return monthlyGoal * 12; // 12 months goal = 360
        case "last7":
          return Math.round(dailyGoal * 7); // 7 days
        case "last30":
          return monthlyGoal; // 30 days = monthly goal
        case "thisMonth": {
          // For current month, calculate goal based on days elapsed in the month
          const today = new Date();
          const daysInCurrentMonth = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0
          ).getDate();
          const daysSoFarInMonth = today.getDate();
          return Math.round(
            (daysSoFarInMonth / daysInCurrentMonth) * monthlyGoal
          );
        }
        case "lastMonth":
          return monthlyGoal; // Full month goal
        default:
          // For custom date ranges, calculate proportional goal
          return Math.round(dailyGoal * daysDiff);
      }
    },

    // Calculate goal progress percentage
    goalProgressPercentage() {
      if (
        !this.chartData ||
        !this.chartData.total_count ||
        this.goalTarget === 0
      ) {
        return 0;
      }
      const percentage = Math.min(
        Math.round((this.chartData.total_count / this.goalTarget) * 100),
        100
      );

      return percentage;
    },

    // Calculate top 3 most popular car models
    topCarModels() {
      if (!this.chartData || !this.chartData.entries || this.chartData.entries.length === 0) {
        return [{ model: "N/A", count: 0 }];
      }

      // Count occurrences of each car model (field "4")
      const carCounts = {};
      
      this.chartData.entries.forEach((entry) => {
        // Get the car model from field "4"
        const carModel = entry["4"];
        
        // Only count if car model exists and is not empty
        if (carModel && carModel.trim() !== "") {
          const modelName = carModel.trim();
          carCounts[modelName] = (carCounts[modelName] || 0) + 1;
        }
      });

      // Convert to array and sort by count (descending)
      const sortedModels = Object.entries(carCounts)
        .map(([model, count]) => ({ model, count }))
        .sort((a, b) => b.count - a.count);

      // Return top 3 models, or fallback if none found
      const topThree = sortedModels.slice(0, 3);
      return topThree.length > 0 ? topThree : [{ model: "N/A", count: 0 }];
    },
  },
  async created() {
    await this.fetchAccountData(this.$router.currentRoute.params.slug);
    if (this.accountData && this.accountData[0]?.form) {
      // Apply last30 preset on initial load
      this.applyDatePreset("last30");
    }
  },

  methods: {
    ...mapActions(["fetchAccountData"]),
    applyDatePreset(preset) {
      this.currentPreset = preset;

      const today = new Date();
      let startDate, endDate;

      switch (preset) {
        case "all":
          // All time - use a very early date (e.g., year 2000) to get all entries
          endDate = new Date(today);
          startDate = new Date(2000, 0, 1); // January 1, 2000
          break;

        case "last12Months":
          // Last 12 months
          endDate = new Date(today);
          startDate = new Date(today);
          startDate.setMonth(startDate.getMonth() - 12); // 12 months back from today
          break;

        case "last7":
          // Last 7 days
          endDate = new Date(today);
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 6); // 7 days including today
          break;

        case "last30":
          // Last 30 days
          endDate = new Date(today);
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 29); // 30 days including today
          break;

        case "thisMonth":
          // This month
          endDate = new Date(today);
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          break;

        case "lastMonth": {
          // Last month
          let lastMonth = today.getMonth() - 1;
          let yearOfLastMonth =
            lastMonth < 0 ? today.getFullYear() - 1 : today.getFullYear();
          let monthIndex = lastMonth < 0 ? 11 : lastMonth;

          // First day of last month
          startDate = new Date(yearOfLastMonth, monthIndex, 1);

          // Last day of last month (first day of current month - 1 day)
          endDate = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        }
      }

      // Update the date range
      this.selectedDateRange = {
        startDate: this.formatDate(startDate),
        endDate: this.formatDate(endDate),
      };

      // Force UI to update
      this.$forceUpdate();

      // Fetch data with new range
      this.fetchData(
        this.selectedDateRange.startDate,
        this.selectedDateRange.endDate
      );
    },

    updateData(newDateRange) {
      // Update selectedDateRange based on user input
      this.selectedDateRange = {
        startDate: newDateRange.startDate,
        endDate: newDateRange.endDate,
      };

      // Force UI to update with the new date range
      this.$forceUpdate();

      // Fetch new data based on the date range
      this.fetchData(
        this.selectedDateRange.startDate,
        this.selectedDateRange.endDate
      );
    },
    formatDate(date) {
      const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
      return formattedDate;
    },
    async fetchData(startDate, endDate) {
      if (!this.accountData || !this.accountData[0]?.form) {
        // Optionally set a timeout to retry
        setTimeout(() => this.fetchData(startDate, endDate), 1000); // retry after 1 second
        return;
      }

      this.isLoading = true; // Start loading

      try {
        const consumerKey = "ck_e3232415b6abca7e07c5a308d4da5498c3d5008e";
        const consumerSecret = "cs_9a8a31739ed97f04c422309918f4dd0e547c7d74";
        const authString = btoa(`${consumerKey}:${consumerSecret}`);

        const searchCriteria = encodeURIComponent(
          JSON.stringify({
            start_date: startDate,
            end_date: endDate,
          })
        );

        const form_id = this.accountData[0]?.form;

        const apiUrl = `https://engagr.driveagent.io/wp-json/gf/v2/forms/${form_id}/entries?search=${searchCriteria}&paging[page_size]=100`;

        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Basic ${authString}`,
          },
        });

        this.chartData = response.data;
        this.Entries = response.data;

        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
      }
    },

    refreshData() {
      this.fetchData(
        this.selectedDateRange.startDate,
        this.selectedDateRange.endDate
      );
    },

    handleTitleFromChild(data) {
      this.formTitle = data;
    },

    handleSalespersonSelected(salesperson) {
      this.selectedSalesperson = salesperson;
    },

    clearSalespersonFilter() {
      this.selectedSalesperson = null;
    },
    clearDateRangeFilter() {
      // Reset to default date range (last 30 days)
      this.applyDatePreset("last30");
    },

    async downloadCSV() {
      if (!this.chartData?.entries || this.chartData.entries.length === 0) {
        return;
      }

      this.isDownloading = true;

      try {
        // Filter entries if salesperson is selected
        let entriesToExport = this.chartData.entries;
        if (this.selectedSalesperson) {
          entriesToExport = entriesToExport.filter(
            (entry) => entry["9"] === this.selectedSalesperson
          );
        }

        // Helper function to find phone field (reused from TotalEntriesList logic)
        const findPhoneField = (entry) => {
          if (
            entry["8"] &&
            String(entry["8"]).trim() !== "" &&
            String(entry["8"]) !== "[]" &&
            String(entry["8"]) !== "null"
          ) {
            return entry["8"];
          }
          if (
            entry["11"] &&
            String(entry["11"]).trim() !== "" &&
            String(entry["11"]) !== "[]" &&
            String(entry["11"]) !== "null"
          ) {
            return entry["11"];
          }
          if (
            entry["10"] &&
            String(entry["10"]).trim() !== "" &&
            String(entry["10"]) !== "[]" &&
            String(entry["10"]) !== "null"
          ) {
            return entry["10"];
          }
          if (
            entry["5"] &&
            String(entry["5"]).trim() !== "" &&
            String(entry["5"]) !== "[]" &&
            String(entry["5"]) !== "null"
          ) {
            return entry["5"];
          }
          return entry["3"];
        };

        // Helper function to format phone (reused from TotalEntriesList logic)
        const formatPhone = (phone) => {
          if (phone === undefined || phone === null) {
            return "No Phone";
          }

          const phoneStr = String(phone);

          if (
            phoneStr.trim() === "" ||
            phoneStr === "[]" ||
            phoneStr === "null"
          ) {
            return "No Phone";
          }

          if (phoneStr.includes("[") && phoneStr.includes("]")) {
            try {
              const parsed = JSON.parse(phoneStr.replace(/'/g, '"'));
              if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed[0] ? String(parsed[0]).trim() : "No Phone";
              }
              return "No Phone";
            } catch (e) {
              return phoneStr.replace(/[[\]']/g, "").trim() || "No Phone";
            }
          }

          return phoneStr.trim();
        };

        // Helper function to format date
        const formatDateForCSV = (inputDate) => {
          const date = new Date(inputDate);
          if (isNaN(date.getTime())) {
            return "Invalid Date";
          }
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
          });
        };

        // Helper function to clean CSV data (escape quotes and remove line breaks)
        const cleanCSVData = (data) => {
          if (data === null || data === undefined) {
            return "";
          }
          const str = String(data);
          // Replace line breaks with spaces and escape quotes
          return str.replace(/[\r\n]+/g, " ").replace(/"/g, '""');
        };

        // Define CSV headers
        const headers = [
          "Name",
          "Email",
          "Phone",
          "Vehicle",
          "Salesperson",
          "Date Created",
          "Entry ID",
        ];

        // Convert entries to CSV format
        const csvRows = [];
        csvRows.push(headers.join(","));

        entriesToExport.forEach((entry) => {
          const phoneField = findPhoneField(entry);
          const formattedPhone = formatPhone(phoneField);

          const row = [
            `"${cleanCSVData(entry["1"] || "N/A")}"`, // Name
            `"${cleanCSVData(entry["2"] || "N/A")}"`, // Email
            `"${cleanCSVData(formattedPhone)}"`, // Phone
            `"${cleanCSVData(entry["4"] || "N/A")}"`, // Vehicle
            `"${cleanCSVData(entry["9"] || "N/A")}"`, // Salesperson
            `"${cleanCSVData(formatDateForCSV(entry["date_created"]))}"`, // Date
            `"${cleanCSVData(entry["id"] || "N/A")}"`, // Entry ID
          ];
          csvRows.push(row.join(","));
        });

        const csvContent = csvRows.join("\n");

        // Create and download the CSV file
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");

        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);

          // Generate filename with dealer name and date range
          const dealerSlug = this.$route.params.slug || "dealership";
          const startDate = this.selectedDateRange.startDate.replace(/-/g, "");
          const endDate = this.selectedDateRange.endDate.replace(/-/g, "");
          const salespersonFilter = this.selectedSalesperson
            ? `_${this.selectedSalesperson.replace(/\s+/g, "_")}`
            : "";
          const filename = `${dealerSlug}_customers_${startDate}_to_${endDate}${salespersonFilter}.csv`;

          link.setAttribute("download", filename);
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        // You could add a user notification here
      } finally {
        this.isDownloading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Custom animations and effects */
@keyframes glow {
  0%,
  100% {
    box-shadow: 0 0 5px rgba(59, 130, 246, 0.5);
  }
  50% {
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.8),
      0 0 30px rgba(59, 130, 246, 0.6);
  }
}

.animate-glow {
  animation: glow 2s ease-in-out infinite;
}

/* Smooth transitions for all interactive elements */
* {
  transition: all 0.2s ease-in-out;
}

/* Enhanced hover effects */
.hover-lift:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

/* Gradient text animation */
.gradient-text {
  background: linear-gradient(45deg, #3b82f6, #8b5cf6, #ec4899);
  background-size: 200% 200%;
  animation: gradient-shift 3s ease infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

@keyframes gradient-shift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

/* Card hover effects */
.card-hover {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.card-hover:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

/* Loading animation enhancement */
.loading-dots {
  display: inline-block;
}

.loading-dots::after {
  content: "";
  animation: dots 1.5s steps(5, end) infinite;
}

@keyframes dots {
  0%,
  20% {
    content: "";
  }
  40% {
    content: ".";
  }
  60% {
    content: "..";
  }
  80%,
  100% {
    content: "...";
  }
}

/* Background image subtle zoom animation */
@keyframes subtle-zoom {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.bg-image-animate {
  animation: subtle-zoom 30s ease-in-out infinite;
}
</style>
